// Evidence Collection tab for Security Insights (Features:GrcReadinessV2).
// Recurring evidence requests (DR test, physical inspection, HR screening + custom) with owner
// assignment, validity windows, automatic reminders, and document upload. Satisfied requests feed
// the readiness score as synthetic "Evidence"-sourced controls (backend EvidenceControlProvider).
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";

const STATUS_CFG = {
    Pending:   { label: "Pending",           cls: "bg-yellow-100 text-yellow-800", dot: "bg-yellow-500" },
    Satisfied: { label: "Satisfied",         cls: "bg-green-100 text-green-800",   dot: "bg-green-500"  },
    Overdue:   { label: "Overdue",           cls: "bg-red-100 text-red-800",       dot: "bg-red-500"    },
    Expired:   { label: "Expired — stale",   cls: "bg-red-100 text-red-800",       dot: "bg-red-500"    },
};

const CATEGORY_CFG = {
    BCDR:     { label: "Business Continuity / DR", cls: "bg-blue-50 text-blue-700 border border-blue-200"   },
    Physical: { label: "Physical Security",        cls: "bg-purple-50 text-purple-700 border border-purple-200" },
    HR:       { label: "HR / Personnel",           cls: "bg-teal-50 text-teal-700 border border-teal-200"   },
    Custom:   { label: "Custom",                   cls: "bg-gray-50 text-gray-600 border border-gray-200"   },
};

const StatusChip = ({ status }) => {
    const c = STATUS_CFG[status] || STATUS_CFG.Pending;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.cls}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />{c.label}
        </span>
    );
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// Recurrence cadences offered in the dropdown; a request's current value is merged in if it
// was set to something else (e.g. a custom request created with 9 months).
const VALIDITY_OPTIONS = [3, 6, 12, 24, 36];
const validityChoices = (current) =>
    VALIDITY_OPTIONS.includes(current) ? VALIDITY_OPTIONS : [...VALIDITY_OPTIONS, current].sort((a, b) => a - b);

const EMPTY_FORM = { title: "", description: "", category: "Custom", ownerUserId: "", validityMonths: 12 };

export default function EvidenceCollectionTab({ canManage, canContribute }) {
    const apiCall = useApi();
    const navigate = useNavigate();

    const [requests,  setRequests]  = useState([]);
    const [users,     setUsers]     = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [error,     setError]     = useState(null);
    const [actionError, setActionError] = useState(null); // dismissible banner for failed actions
    const [uploading, setUploading] = useState({});   // { requestId: bool }
    const [analyzing, setAnalyzing] = useState({});   // { docId: bool }
    const [removing,  setRemoving]  = useState({});   // { docId: bool }
    const [showCreate, setShowCreate] = useState(false);
    const [form,      setForm]      = useState(EMPTY_FORM);
    const [saving,    setSaving]    = useState(false);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [reqs, assignable] = await Promise.all([
                apiCall("GET", "/security-insights/evidence/requests"),
                apiCall("GET", "/assessments/assignableUsers").catch(() => []),
            ]);
            setRequests(reqs || []);
            setUsers(assignable || []);
            setError(null);
        } catch {
            setError("Could not load evidence requests.");
        } finally {
            setLoading(false);
        }
    }, [apiCall]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const patchRequest = (updated) =>
        setRequests(p => p.map(r => (r.id === updated.id ? updated : r)));

    const assignOwner = async (request, ownerUserId) => {
        const prev = requests;
        try {
            const updated = await apiCall("PUT", `/security-insights/evidence/requests/${request.id}`,
                { ownerUserId: ownerUserId ? Number(ownerUserId) : null });
            patchRequest(updated);
        } catch { setRequests(prev); }
    };

    const changeValidity = async (request, months) => {
        const prev = requests;
        try {
            // Backend re-derives the due date under the new cadence (from the last upload when
            // Satisfied, from now otherwise) — the response carries the updated dates.
            const updated = await apiCall("PUT", `/security-insights/evidence/requests/${request.id}`,
                { validityMonths: Number(months) });
            patchRequest(updated);
        } catch (e) {
            setRequests(prev);
            setActionError(e?.response?.data?.message || "Could not change the recurrence window.");
        }
    };

    const uploadDoc = async (request, file) => {
        setUploading(p => ({ ...p, [request.id]: true }));
        try {
            const formData = new FormData();
            formData.append("file", file);
            await apiCall("POST", `/security-insights/evidence/requests/${request.id}/documents`, formData, true);
            await fetchData(); // status + due date changed server-side
        } catch (e) {
            setActionError(e?.response?.data?.message || "Upload failed.");
        } finally {
            setUploading(p => ({ ...p, [request.id]: false }));
        }
    };

    const deleteDoc = async (doc) => {
        setRemoving(p => ({ ...p, [doc.id]: true }));
        try {
            await apiCall("DELETE", `/security-insights/evidence/documents/${doc.id}`);
            await fetchData(); // last-doc deletion reverts the request to Pending server-side
        } catch {
            setRemoving(p => ({ ...p, [doc.id]: false }));
        }
    };

    const analyzeDoc = async (doc) => {
        setAnalyzing(p => ({ ...p, [doc.id]: true }));
        try {
            await apiCall("POST", `/security-insights/evidence/documents/${doc.id}/analyze`);
            await fetchData();
        } catch { /* analysis failure recorded server-side */ }
        finally { setAnalyzing(p => ({ ...p, [doc.id]: false })); }
    };

    const createRequest = async () => {
        if (!form.title.trim()) return;
        setSaving(true);
        try {
            await apiCall("POST", "/security-insights/evidence/requests", {
                title: form.title.trim(),
                description: form.description || null,
                category: form.category,
                ownerUserId: form.ownerUserId ? Number(form.ownerUserId) : null,
                validityMonths: Number(form.validityMonths) || 12,
            });
            setShowCreate(false);
            setForm(EMPTY_FORM);
            await fetchData();
        } catch (e) {
            setActionError(e?.response?.data?.message || "Could not create the evidence request.");
        } finally { setSaving(false); }
    };

    const deleteRequest = async (request) => {
        try {
            await apiCall("DELETE", `/security-insights/evidence/requests/${request.id}`);
            setRequests(p => p.filter(r => r.id !== request.id));
        } catch { /* keep state */ }
    };

    const counts = requests.reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {});
    const grouped = Object.keys(CATEGORY_CFG)
        .map(cat => ({ cat, items: requests.filter(r => r.category === cat) }))
        .filter(g => g.items.length > 0);

    if (loading) return <div className="max-w-7xl mx-auto px-5 py-10 text-sm text-gray-400">Loading evidence requests…</div>;
    if (error)   return <div className="max-w-7xl mx-auto px-5 py-10 text-sm text-red-500">{error}</div>;

    return (
        <div className="max-w-7xl mx-auto px-5 pb-8 pt-4">
            {/* Action-failure banner */}
            {actionError && (
                <div className="flex items-center justify-between gap-3 mb-4 px-4 py-2.5 bg-red-50 border border-red-200 rounded-lg">
                    <span className="text-xs text-red-700">{actionError}</span>
                    <button onClick={() => setActionError(null)} className="text-xs font-semibold text-red-400 hover:text-red-700 flex-shrink-0">✕</button>
                </div>
            )}

            {/* Stats strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                {[
                    { label: "Evidence requests", value: requests.length, color: "text-indigo-600" },
                    { label: "Satisfied", value: counts.Satisfied || 0, color: "text-green-600" },
                    { label: "Pending", value: counts.Pending || 0, color: "text-yellow-600" },
                    { label: "Overdue / Expired", value: (counts.Overdue || 0) + (counts.Expired || 0), color: "text-red-600" },
                ].map(m => (
                    <div key={m.label} className="bg-white border border-gray-200 rounded-xl p-4">
                        <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">{m.label}</div>
                        <div className={`text-2xl font-bold ${m.color}`}>{m.value}</div>
                    </div>
                ))}
            </div>

            <div className="flex items-center justify-between mb-4">
                <p className="text-xs text-gray-500 max-w-2xl">
                    Recurring evidence requests cover the audit domains no connector can see — BC/DR testing,
                    physical security, and HR screening. Uploaded evidence stays valid for the request's validity
                    window; owners get automatic reminders before it expires.
                </p>
                {canManage && (
                    <button onClick={() => setShowCreate(true)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 flex-shrink-0">
                        + New Evidence Request
                    </button>
                )}
            </div>

            {/* Request groups */}
            {grouped.map(({ cat, items }) => (
                <div key={cat} className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${CATEGORY_CFG[cat].cls}`}>{CATEGORY_CFG[cat].label}</span>
                        <span className="text-xs text-gray-400">{items.length} request{items.length === 1 ? "" : "s"}</span>
                    </div>
                    <div className="space-y-3">
                        {items.map(r => {
                            const isUploading = uploading[r.id];
                            const borderCls = r.status === "Satisfied" ? "border-l-4 border-l-green-500 border-gray-200"
                                : r.status === "Pending" ? "border-l-4 border-l-yellow-500 border-gray-200"
                                : "border-l-4 border-l-red-500 border-gray-200";
                            return (
                                <div key={r.id} className={`bg-white border rounded-xl p-4 ${borderCls}`}>
                                    <div className="flex items-start justify-between gap-3 mb-1.5">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm font-medium text-gray-800">{r.title}</span>
                                                <StatusChip status={r.status} />
                                                {canManage && r.grcRiskId && (
                                                    <button onClick={() => navigate(`/risks?riskId=${r.grcRiskId}`)}
                                                        title="This unmet evidence request is an open GRC risk"
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100">
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                                                        Open GRC risk →
                                                    </button>
                                                )}
                                            </div>
                                            {r.description && <div className="text-xs text-gray-500 mt-1">{r.description}</div>}
                                        </div>
                                        {canManage && !r.isTemplate && (
                                            <button onClick={() => deleteRequest(r)} title="Delete custom request"
                                                className="text-xs text-gray-400 hover:text-red-600 flex-shrink-0">✕</button>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-4 flex-wrap text-xs text-gray-500 mb-3">
                                        <span title={r.status === "Satisfied" ? "Evidence valid until this date" : "Evidence due by this date"}>
                                            {r.status === "Satisfied" ? "Valid until" : "Due"}: <strong className="text-gray-700">{fmtDate(r.dueDate)}</strong>
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            Recurs every
                                            {canManage ? (
                                                <select value={r.validityMonths} onChange={e => changeValidity(r, e.target.value)}
                                                    title="How long uploaded evidence stays valid — changing it re-derives the due date"
                                                    className="border border-gray-200 rounded-md px-1.5 py-0.5 text-xs bg-white text-gray-700">
                                                    {validityChoices(r.validityMonths).map(m => (
                                                        <option key={m} value={m}>{m} months</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <strong className="text-gray-700">{r.validityMonths} months</strong>
                                            )}
                                        </span>
                                        {r.lastSatisfiedAt && <span>Last evidence: <strong className="text-gray-700">{fmtDate(r.lastSatisfiedAt)}</strong></span>}
                                        <span className="flex items-center gap-1.5">
                                            Owner:
                                            {canManage ? (
                                                <select value={r.ownerUserId || ""} onChange={e => assignOwner(r, e.target.value)}
                                                    className="border border-gray-200 rounded-md px-1.5 py-0.5 text-xs bg-white text-gray-700">
                                                    <option value="">Unassigned</option>
                                                    {users.map(u => <option key={u.id} value={u.id}>{u.fullName}</option>)}
                                                </select>
                                            ) : (
                                                <strong className="text-gray-700">{r.ownerName || "Unassigned"}</strong>
                                            )}
                                        </span>
                                    </div>

                                    {/* Documents */}
                                    <div className="border-t border-gray-100 pt-3">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                Evidence Documents {r.documents.length > 0 && <span className="ml-1 bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-bold">{r.documents.length}</span>}
                                            </span>
                                            {canContribute && (
                                                <label title="Upload PDF, DOCX, TXT or CSV — max 10 MB"
                                                    className={`group relative flex items-center gap-1.5 text-xs px-3 py-1.5 border rounded-lg font-semibold select-none transition-all duration-150
                                                        ${isUploading
                                                            ? "bg-indigo-50 text-indigo-400 border-indigo-200 cursor-not-allowed"
                                                            : "bg-white text-indigo-700 border-indigo-300 cursor-pointer hover:bg-indigo-600 hover:text-white hover:border-indigo-600 hover:shadow-md active:scale-95"}`}>
                                                    {isUploading ? (
                                                        <>
                                                            <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a10 10 0 100 10z"/>
                                                            </svg>
                                                            Uploading…
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
                                                            </svg>
                                                            Upload Evidence
                                                        </>
                                                    )}
                                                    <input type="file" className="hidden" accept=".pdf,.docx,.txt,.csv" disabled={isUploading}
                                                        onChange={e => { const f = e.target.files?.[0]; if (f) { uploadDoc(r, f); e.target.value = ""; } }} />
                                                </label>
                                            )}
                                        </div>

                                        {r.documents.length === 0 && !isUploading && (
                                            <div className="flex items-center gap-2 py-3 px-3 bg-gray-50 border border-dashed border-gray-200 rounded-lg">
                                                <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                                <span className="text-xs text-gray-400 italic">No evidence on file — upload PDF, DOCX, TXT, or CSV to satisfy this request.</span>
                                            </div>
                                        )}

                                        {isUploading && r.documents.length === 0 && (
                                            <div className="flex items-center gap-2 py-3 px-3 bg-indigo-50 border border-indigo-100 rounded-lg animate-pulse">
                                                <svg className="w-4 h-4 text-indigo-400 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a10 10 0 100 10z"/></svg>
                                                <span className="text-xs text-indigo-500">Uploading and scanning for malware…</span>
                                            </div>
                                        )}

                                        {r.documents.length > 0 && (
                                            <div className="space-y-2">
                                                {r.documents.map(doc => {
                                                    const isAnalyzing = analyzing[doc.id] || doc.analysisStatus === "Analyzing";
                                                    const isRemoving  = removing[doc.id];
                                                    const isDone      = doc.analysisStatus === "Completed";
                                                    const isFailed    = doc.analysisStatus === "Failed";

                                                    return (
                                                        <div key={doc.id} className={`border rounded-lg p-3 transition-all duration-200 ${isRemoving ? "opacity-40 scale-95 pointer-events-none" : "bg-white border-gray-200 hover:border-indigo-200 hover:shadow-sm"}`}>
                                                            <div className="flex items-center justify-between gap-2">
                                                                {/* File info */}
                                                                <div className="flex items-center gap-2 min-w-0">
                                                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${isDone ? "bg-green-100" : isFailed ? "bg-red-100" : isAnalyzing ? "bg-indigo-100" : "bg-gray-100"}`}>
                                                                        {isAnalyzing
                                                                            ? <svg className="w-3.5 h-3.5 text-indigo-500 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a10 10 0 100 10z"/></svg>
                                                                            : <svg className={`w-3.5 h-3.5 ${isDone ? "text-green-600" : isFailed ? "text-red-500" : "text-gray-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>}
                                                                    </div>
                                                                    <div className="min-w-0">
                                                                        <div className="text-xs font-medium text-gray-800 truncate">{doc.documentName}</div>
                                                                        <div className="text-xs text-gray-400">Uploaded {fmtDate(doc.uploadedAt)}{doc.uploadedByName ? ` by ${doc.uploadedByName}` : ""}{doc.fileSize ? ` · ${(doc.fileSize / 1024).toFixed(0)} KB` : ""}</div>
                                                                    </div>
                                                                </div>

                                                                {/* Action buttons */}
                                                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                                                    {/* Scan badge */}
                                                                    <span title={doc.malwareScanStatus === "ScanError" ? "The malware scanner was unavailable when this file was checked." : `Malware scan: ${doc.malwareScanStatus}`}
                                                                        className={`text-xs px-1.5 py-0.5 rounded-full font-semibold select-none
                                                                            ${doc.malwareScanStatus === "Clean"     ? "bg-green-100 text-green-700"
                                                                            : doc.malwareScanStatus === "Malicious" ? "bg-red-100 text-red-700"
                                                                            : doc.malwareScanStatus === "NoScanners"? "bg-gray-100 text-gray-500"
                                                                            : doc.malwareScanStatus === "ScanError" ? "bg-gray-100 text-gray-500"
                                                                            : "bg-yellow-100 text-yellow-700"}`}>
                                                                        {doc.malwareScanStatus === "Clean" ? "✓ Scanned" : doc.malwareScanStatus === "Malicious" ? "✕ Malicious" : doc.malwareScanStatus === "NoScanners" ? "No scan" : doc.malwareScanStatus === "ScanError" ? "Not scanned" : "Scanning…"}
                                                                    </span>

                                                                    {/* Analyze button */}
                                                                    {canContribute && doc.malwareScanStatus !== "Malicious" && (
                                                                        <button
                                                                            onClick={() => analyzeDoc(doc)}
                                                                            disabled={isAnalyzing || isDone}
                                                                            title={isDone ? "Analysis complete" : isAnalyzing ? "Analyzing document with AI…" : "Analyze this document with Azure OpenAI"}
                                                                            className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border font-semibold transition-all duration-150 select-none
                                                                                ${isDone      ? "bg-green-50 text-green-700 border-green-200 cursor-default"
                                                                                : isAnalyzing ? "bg-indigo-50 text-indigo-400 border-indigo-200 cursor-not-allowed"
                                                                                : "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 hover:shadow-md active:scale-95"}`}>
                                                                            {isAnalyzing ? (
                                                                                <><svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a10 10 0 100 10z"/></svg> Analyzing…</>
                                                                            ) : isDone ? (
                                                                                <><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg> Analyzed</>
                                                                            ) : (
                                                                                <><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg> Analyze with AI</>
                                                                            )}
                                                                        </button>
                                                                    )}

                                                                    {/* Remove button */}
                                                                    {canContribute && (
                                                                        <button
                                                                            onClick={() => deleteDoc(doc)}
                                                                            disabled={isRemoving || isAnalyzing}
                                                                            title={isAnalyzing ? "Cannot remove while analyzing" : "Remove this document"}
                                                                            className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border font-semibold transition-all duration-150 select-none
                                                                                ${isRemoving || isAnalyzing
                                                                                    ? "bg-gray-50 text-gray-300 border-gray-200 cursor-not-allowed"
                                                                                    : "bg-white text-red-500 border-red-200 hover:bg-red-600 hover:text-white hover:border-red-600 hover:shadow-md active:scale-95"}`}>
                                                                            {isRemoving ? (
                                                                                <><svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a10 10 0 100 10z"/></svg> Removing…</>
                                                                            ) : (
                                                                                <><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg> Remove</>
                                                                            )}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Analyzing progress bar */}
                                                            {isAnalyzing && (
                                                                <div className="mt-2 h-1 bg-indigo-100 rounded-full overflow-hidden">
                                                                    <div className="h-full bg-indigo-500 rounded-full animate-pulse" style={{width: "100%"}}/>
                                                                </div>
                                                            )}

                                                            {/* AI analysis result */}
                                                            {isDone && doc.analysisResult && (
                                                                <div className="mt-3 border-t border-gray-100 pt-2">
                                                                    <div className="flex items-center gap-1.5 mb-1.5">
                                                                        <svg className="w-3.5 h-3.5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
                                                                        <span className="text-xs font-semibold text-indigo-700">AI Analysis</span>
                                                                        {doc.analyzedAt && <span className="text-xs text-gray-400 ml-auto">{new Date(doc.analyzedAt).toLocaleString()}</span>}
                                                                    </div>
                                                                    <div className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-2">{doc.analysisResult}</div>
                                                                </div>
                                                            )}
                                                            {isFailed && (
                                                                <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600 border-t border-red-100 pt-2">
                                                                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                                                    {doc.analysisResult || "Analysis failed. Please try again."}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            {/* Create modal */}
            {showCreate && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => !saving && setShowCreate(false)}>
                    <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
                        <h3 className="text-sm font-bold text-gray-800 mb-4">New Evidence Request</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs font-semibold text-gray-500 block mb-1">Title *</label>
                                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                                    placeholder="e.g. Upload Penetration Test Report"
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-500 block mb-1">Description</label>
                                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                    rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 block mb-1">Category</label>
                                    <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                                        {Object.entries(CATEGORY_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 block mb-1">Valid for</label>
                                    <select value={form.validityMonths}
                                        onChange={e => setForm(f => ({ ...f, validityMonths: e.target.value }))}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                                        {VALIDITY_OPTIONS.map(m => <option key={m} value={m}>{m} months</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-gray-500 block mb-1">Owner</label>
                                <select value={form.ownerUserId} onChange={e => setForm(f => ({ ...f, ownerUserId: e.target.value }))}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                                    <option value="">Unassigned</option>
                                    {users.map(u => <option key={u.id} value={u.id}>{u.fullName}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button onClick={() => setShowCreate(false)} disabled={saving}
                                className="px-4 py-2 rounded-lg text-xs font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
                            <button onClick={createRequest} disabled={saving || !form.title.trim()}
                                className="px-4 py-2 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                                {saving ? "Creating…" : "Create Request"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
